import { Trans as _Trans } from "../lib/i18n/Trans";
import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import { AlertCircle, Bell01, CheckCircle, InfoCircle, XCircle, XClose } from "../icons/v2-stroke";
import { assertNever } from "../lib/internal_utils";
import { cn } from "../lib/utils";
import React from "react";
import { Toaster, toast } from "sonner";
import { Button } from "./Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./Collapsible";
import { Icon } from "./Icon";
const NOTIFICATION_DELAY_MS = 5000;
const NotificationsContext = React.createContext(n => n);
function resolveIcon(type) {
  switch (type) {
    case "success":
      return CheckCircle;
    case "error":
      return XCircle;
    case "info":
      return InfoCircle;
    case "warning":
      return AlertCircle;
    case "hello":
      return Bell01;
    default:
      return assertNever(type);
  }
}
function resolveSurface(type) {
  switch (type) {
    case "success":
      return "bg-success-50 border-success-200 dark:bg-green-950 dark:border-green-900";
    case "error":
      return "bg-red-50 border-rose-100 dark:bg-red-950 dark:border-red-900";
    case "info":
      return "bg-highlight-50 border-highlight-100 dark:bg-blue-950 dark:border-blue-900";
    case "warning":
      return "bg-orange-50 border-orange-100 dark:bg-golden-950 dark:border-golden-900";
    case "hello":
      return "bg-stone-50 border-stone-150 dark:bg-stone-950 dark:border-stone-800";
    default:
      return assertNever(type);
  }
}
function resolveForeground(type) {
  switch (type) {
    case "success":
      return "text-success-800 dark:text-green-100";
    case "error":
      return "text-red-800 dark:text-red-100";
    case "info":
      return "text-highlight-800 dark:text-blue-100";
    case "warning":
      return "text-orange-800 dark:text-golden-100";
    case "hello":
      return "text-stone-800 dark:text-stone-50";
    default:
      return assertNever(type);
  }
}

/**
 * The presentational card of a toast notification: icon, title, description,
 * optional collapsible details, optional action button, and optional dismiss control. Use it directly for
 * inline previews; in product code dispatch toasts with useSendNotification
 * instead.
 * @summary Presentational notification card.
 */
export function NotificationContent({
  type,
  title,
  description,
  details,
  action,
  onDismiss,
  onToggleDetails
}) {
  const {
    _: _t
  } = _useLingui();
  const icon = resolveIcon(type);
  const foreground = resolveForeground(type);
  return /*#__PURE__*/React.createElement("div", {
    className: cn("pointer-events-auto relative flex w-[264px] flex-col gap-2 overflow-clip", "rounded-xl border p-3", resolveSurface(type), "[&>*]:transition-opacity [&>*]:duration-400 [&>*]:ease-enter", "[[data-expanded=false][data-front=false]_&>*]:opacity-0")
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-start justify-between gap-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex min-w-0 flex-1 items-start gap-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "mt-[2px] shrink-0"
  }, /*#__PURE__*/React.createElement(Icon, {
    visual: icon,
    size: "xs",
    className: foreground,
    "aria-hidden": "true"
  })), /*#__PURE__*/React.createElement("div", {
    className: "flex min-w-0 flex-col gap-0.5"
  }, title && /*#__PURE__*/React.createElement("span", {
    className: cn("label-sm", foreground)
  }, title), description && /*#__PURE__*/React.createElement("span", {
    className: cn("copy-xs opacity-80", foreground)
  }, description))), onDismiss && /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: onDismiss,
    className: cn("mt-[2px] shrink-0 cursor-pointer opacity-60 transition-opacity hover:opacity-100", foreground),
    "aria-label": _t(
    /** i18n */
    {
      id: "dj2xTE",
      message: "Dismiss notification"
    })
  }, /*#__PURE__*/React.createElement(Icon, {
    visual: XClose,
    size: "xs"
  }))), details && /*#__PURE__*/React.createElement(Collapsible, {
    className: "pl-6",
    onOpenChange: onToggleDetails
  }, /*#__PURE__*/React.createElement(CollapsibleTrigger, null, /*#__PURE__*/React.createElement("span", {
    className: cn("copy-xs", foreground)
  }, /*#__PURE__*/React.createElement(_Trans,
  /** i18n */
  {
    id: "URmyfc",
    message: "Details"
  }))), /*#__PURE__*/React.createElement(CollapsibleContent, {
    animated: false
  }, /*#__PURE__*/React.createElement("pre", {
    className: cn("copy-xs mt-1 max-h-40 select-text overflow-auto whitespace-pre-wrap break-words font-mono opacity-80", foreground)
  }, details))), action && /*#__PURE__*/React.createElement("div", {
    className: "pl-6"
  }, /*#__PURE__*/React.createElement(Button, {
    size: "xs",
    variant: "outline",
    label: action.label,
    onClick: () => {
      action.onClick();
      onDismiss === null || onDismiss === void 0 || onDismiss();
    }
  })));
}

/**
 * A transient toast system that confirms the outcome of an action. Mount a
 * single Notification.Area near the app root, then dispatch toasts
 * imperatively with the useSendNotification hook. Use it for brief,
 * self-dismissing feedback after an action completes; for persistent, inline
 * status attached to a region, use ContentMessage instead.
 * @summary Toast notifications dispatched via hook.
 */
export const Notification = {
  Area: ({
    children
  }) => {
    const sendNotification = React.useCallback(notification => {
      let detailsToggleCount = 0;
      const render = id => /*#__PURE__*/React.createElement(NotificationContent, {
        type: notification.type,
        title: notification.title,
        description: notification.description,
        details: notification.details,
        action: notification.action,
        onDismiss: () => toast.dismiss(id),
        onToggleDetails: () => {
          detailsToggleCount += 1;
          // Update the toast in place without a dismiss timer, so someone reading the details
          // doesn't lose it. Sonner only re-measures a toast's height when its `description`
          // changes (custom toasts don't render it), and caps the toast to that height while
          // the stack is expanded on hover: a new value makes it fit the toggled details.
          toast.custom(render, {
            id,
            duration: Infinity,
            description: String(detailsToggleCount)
          });
        }
      });
      toast.custom(render, {
        duration: NOTIFICATION_DELAY_MS
      });
    }, []);
    return /*#__PURE__*/React.createElement(NotificationsContext.Provider, {
      value: sendNotification
    }, children, /*#__PURE__*/React.createElement(Toaster, {
      className: "flex flex-col items-end",
      duration: NOTIFICATION_DELAY_MS,
      visibleToasts: 3,
      closeButton: false,
      expand: false,
      invert: false,
      swipeDirections: ["right"],
      toastOptions: {
        unstyled: true,
        // Sonner measures a toast by setting its height to `auto` and reading it back. Apps
        // that set `interpolate-size: allow-keywords` make that change animate, so the read
        // returns the old height and the hovered stack clips the toast.
        className: "w-fit select-none [interpolate-size:numeric-only]"
      }
    }));
  }
};

/**
 * Returns the function that dispatches a toast notification, provided by the
 * nearest Notification.Area.
 * @summary Hook to dispatch toast notifications.
 */
export const useSendNotification = () => React.useContext(NotificationsContext);
//# sourceMappingURL=Notification.js.map